/*
  Foxkit quantity-breaks shim (CS-53).

  Two live defects, both inside the Foxkit app's own CDN asset `quantity-breaks.js` (extension
  version `foxkit-upsell-bundles-469`), both reproduced on every run and both root-caused in
  `Website/docs/incidents/foxkit-multibuy-variant-2026-08-11.md`. Neither is reachable from theme
  code and neither depends on anything the theme does.

  DEFECT 1, the wrong colour, silently. The app's `ON_VARIANT_CHANGE` handler fans out to every
  tier, and each tier's `onChange()` force-writes the single product-level variant into EVERY
  per-item dropdown:

      onChange(){ this.variant = this.container?.selectedVariant,
        this.variant && (this.variantSelects.length &&
          this.variantSelects.forEach(t => { t.value = this.variant.id }),
          this.checkQuantityRule(), this.updatePrices()) }

  The shopper's second colour is overwritten with no message and nothing to undo. Worse, that
  method never calls `updateSelectedItems()`, and assigning `.value` from script fires no `change`,
  so the tier's own listener does not run either: `selectedItems` keeps whatever it last computed
  and `handleSubmit()` posts it directly. Measured: the page read Dark Pink on the swatch and on
  both dropdowns while the request posted `{"items":[{"id":"39398070681683","quantity":2}]}`, which
  is Baby Pink. That is a wrong-goods-shipped path, silent to the shopper and to us.

  DEFECT 2, the discount silently does not apply. `updateQuantity()`, which pushes the tier's
  `min_qty` into the theme's product form, is called from the container's radio `change` handler
  and, at init, only when `layout === 'card'`. All four offers on this store are `rich`, and every
  offer renders its first tier already checked (`default_selected: 0`), so a shopper who accepts
  the preselected tier never fires a `change`: the form keeps quantity 1 and "Get this deal!" adds
  ONE unit at full price. This is the branch the Ganesha, Wedding Cards and Seed Paper offers take,
  134 products, and Wedding Cards preselects the 50 unit tier.

  WHAT THIS DOES. It subscribes to Foxkit's own event bus (`window.FoxkitEvents`), which is the
  pattern `assets/foxkit-cart-bridge.js` (CS-21) has run on since the 8 August release, and repairs
  the state the app leaves behind. It patches no app code, forks no app file and duplicates no
  theme handler. Every entry point feature-detects and stands down silently, so a page with no
  offer, a page where the app never loads, or a future extension version with a different API shape
  gets a no-op rather than an exception.

  RE-VERIFY ON EVERY FOXKIT EXTENSION UPDATE. `updateSelectedItems`, `updatePrices` and
  `updateQuantity` are the app's own methods: stable in 469, but undocumented and unowned by us.
  The harness is `Website/scripts/foxkit-multibuy-probe-cdp.js`.
*/
(() => {
  const POLL_MS = 300;
  const GIVE_UP_MS = 20000; // The app bundle loads after the theme assets, then fetches its config.

  // What the shopper actually chose in each per-item dropdown, keyed by the dropdown itself. A
  // dropdown the shopper has never touched is deliberately absent, so it stays free to follow the
  // product's own colour swatch, which is what that control is for. Only a real choice is defended.
  const chosen = new WeakMap();

  document.addEventListener(
    'change',
    (event) => {
      const select = event.target;
      if (!select || select.tagName !== 'SELECT' || typeof select.closest !== 'function') return;
      if (!select.closest('foxkit-quantity-breaks-choice')) return;
      chosen.set(select, select.value);
    },
    true
  );

  // Defect 1. Restore what the shopper chose, then recompute the tier's basket from what is now on
  // screen, so the request can no longer disagree with the page.
  function repairTiers() {
    document.querySelectorAll('foxkit-quantity-breaks-choice').forEach((choice) => {
      const selects = Array.from(choice.querySelectorAll('select'));
      if (!selects.length || typeof choice.updateSelectedItems !== 'function') return;

      selects.forEach((select) => {
        const wanted = chosen.get(select);
        if (wanted === undefined || select.value === wanted) return;
        // The variant the shopper picked may not be offered any more. Leaving the app's own value
        // in place is correct; forcing a value the <select> does not carry would blank it.
        if (!Array.from(select.options).some((option) => option.value === wanted)) return;
        select.value = wanted;
      });

      // A blank dropdown cannot produce a line item, and the app's own updatePrices() throws on an
      // id it cannot find in productJson. Leave the widget exactly as it is rather than half-repair.
      if (selects.some((select) => !select.value)) return;

      choice.updateSelectedItems();
      if (typeof choice.updatePrices === 'function') choice.updatePrices();
    });
  }

  // Defect 2. Give the preselected tier the state a clicked tier would have had.
  // Containers whose form quantity has been confirmed to match the selected tier, and containers
  // whose quantity input the shopper has edited. Both stop us from touching the input again.
  const primed = new WeakSet();
  const shopperEdited = new WeakSet();

  // The quantity input the theme's product form submits, for a given widget. The last fallback is
  // not laziness: on this theme the input is associated with the form by attribute rather than by
  // nesting, so a form-scoped lookup alone returns null on a product page that plainly has one, and
  // the confirmation below would then stand down on a container it had not actually finished.
  function quantityInput(container) {
    const form = container.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');
    return (form && form.querySelector('[name="quantity"]')) ||
      document.querySelector('[name="quantity"]');
  }

  // A programmatic value assignment fires no input event, so this only ever hears a real shopper
  // (or the theme's own stepper buttons, which is the same intent). Once heard, the tier stops
  // rewriting the box: a shim that keeps overwriting a typed quantity is a defect, not a fix.
  document.addEventListener(
    'input',
    (event) => {
      const el = event.target;
      if (!el || el.name !== 'quantity') return;
      document.querySelectorAll('foxkit-quantity-breaks').forEach((c) => {
        if (quantityInput(c) === el) shopperEdited.add(c);
      });
    },
    true
  );
  function primePreselectedTier() {
    document.querySelectorAll('foxkit-quantity-breaks').forEach((container) => {
      const config = container.config;
      // No config means the app has not initialised this element, or has no active offer for this
      // product and is about to destroy it. Either way there is nothing here to repair.
      if (!config || !container.domNodes || primed.has(container)) return;
      if (shopperEdited.has(container)) return;

      const choices = container.domNodes.choices || [];
      const choice = choices[container.selectedIndex] || choices[0];
      if (
        choice &&
        typeof choice.updateSelectedItems === 'function' &&
        !(choice.selectedItems || []).length
      ) {
        choice.updateSelectedItems();
      }

      // Only the branch that submits through the theme's product form reads that quantity input.
      // This is the app's own condition in handleSubmit(), inverted, so the rich offer with variant
      // selectors (which submits its own selectedItems) keeps the form untouched.
      if (config.layout === 'rich' && config.show_variant_selector) {
        primed.add(container);
        return;
      }
      if (typeof container.updateQuantity !== 'function') return;
      container.updateQuantity();

      // Only now is the container finished with. A widget that renders its config before its
      // choices, which is what a product with hundreds of variants does, would otherwise take its
      // one attempt too early and then re-render the box back to the product's default: measured on
      // a wedding card, where the "Buy 50" tier was leaving 25 in the box on both themes. Confirm the
      // box actually reads the selected tier's minimum before standing down; the outer poll is
      // already bounded, so an offer whose shape we cannot read simply gets retried until it expires.
      // Deliberately NOT primed here, so the bounded poll keeps re-asserting this for its whole
      // window. Measured on a wedding card: setting the box once is not enough, because the order
      // limits app initialises after the offer widget and writes its own minimum (25) back over the
      // tier's (50). Confirming the value immediately after our own call reads 50 and is a lie a
      // second later. Re-asserting costs one method call per tick on a page that has an offer, it
      // stops the moment the shopper touches the box, and it stops for good when the poll expires.
    });
  }

  function guard(fn) {
    try {
      fn();
    } catch (error) {
      // ponytail: standing down leaves the shopper exactly the widget they have on the live site
      // today. A shim that throws on a page it does not understand is worse than the defect.
    }
  }

  let subscribed = false;
  function subscribe() {
    if (!window.FoxkitEvents || typeof window.FoxkitEvents.subscribe !== 'function') return false;
    // The app's handler runs synchronously inside the emit, so one frame later it has finished
    // whichever order the two subscriptions were made in. Nothing here depends on winning a race.
    window.FoxkitEvents.subscribe('ON_VARIANT_CHANGE', () =>
      requestAnimationFrame(() => guard(repairTiers))
    );
    return true;
  }

  // ponytail: a flat bounded poll for the app's async init, the same shape and budget
  // foxkit-cart-bridge.js uses on the same bus. Both jobs are idempotent, so a tick that finds
  // nothing costs one querySelectorAll, which is all a product page with no offer ever pays.
  const poll = setInterval(() => {
    if (!subscribed) subscribed = subscribe();
    guard(primePreselectedTier);
  }, POLL_MS);
  setTimeout(() => clearInterval(poll), GIVE_UP_MS);
})();
